(() => {
  const key = 'staffAttributionV1';
  const ttl = 86400000;
  const navigationType = performance.getEntriesByType?.('navigation')?.[0]?.type;
  const candidate = navigationType === 'reload' || navigationType === 'back_forward'
    ? null : window.staffAttributionCandidate || null;
  const root = window.Shopify?.routes?.root || '/';
  let state = { status: 'unknown' };
  let cartWork = Promise.resolve();
  let cartController = new AbortController();
  let cartEpoch = 0;
  let expiryTimer;
  let privacyLoaded = false;
  let privacyLoading = false;
  let candidateUsed = false;
  let lastKnownRecord = null;
  let fallbackRecord = null;

  const validId = (value) => typeof value === 'string' && /^[0-9]{3,4}$/.test(value);
  const privacyStatus = () => {
    if (!privacyLoaded) return 'unknown';
    try {
      const result = window.Shopify.customerPrivacy.marketingAllowed();
      return result === true ? 'allowed' : result === false ? 'denied' : 'unknown';
    } catch { return 'unknown'; }
  };
  const activeId = () => state.status === 'active' && performance.now() < state.deadline ? state.staffId : null;
  const setField = (value) => {
    const field = document.querySelector('#Stuff[name="attributes[Stuff]"]');
    if (field) field.value = value;
  };
  const forget = () => { try { localStorage.removeItem(key); } catch { /* Storage can be unavailable. */ } };
  const readRecord = () => {
    let raw;
    try { raw = localStorage.getItem(key); }
    catch { return { status: 'unavailable' }; }
    let record;
    try { record = JSON.parse(raw || 'null'); }
    catch { forget(); return { status: 'none' }; }
    try {
      if (!record) return { status: 'none' };
      if (![1, 2].includes(record.version) || !validId(record.staffId) ||
          !Number.isSafeInteger(record.acceptedAt) || !Number.isSafeInteger(record.expiresAt) ||
          record.expiresAt !== record.acceptedAt + ttl || Date.now() < record.acceptedAt ||
          Date.now() >= record.expiresAt) { forget(); return { status: 'none' }; }
      return { status: 'active', record };
    } catch { return { status: 'unavailable' }; }
  };
  const writeRecord = (record) => {
    try { localStorage.setItem(key, JSON.stringify(record)); return true; }
    catch { return false; }
  };
  const stopCartWork = () => {
    cartController.abort();
    cartController = new AbortController();
    cartEpoch++;
  };
  const setState = (record) => {
    clearTimeout(expiryTimer);
    if (!record) { state = { status: 'none' }; return; }
    const remaining = Math.max(0, record.expiresAt - Date.now());
    const deadline = state.status === 'active' && state.staffId === record.staffId &&
      state.acceptedAt === record.acceptedAt
      ? Math.min(state.deadline, performance.now() + remaining) : performance.now() + remaining;
    state = { status: 'active', staffId: record.staffId, acceptedAt: record.acceptedAt, deadline };
    const current = state;
    expiryTimer = setTimeout(() => {
      if (state !== current || activeId()) return;
      const stored = readRecord();
      if (stored.status === 'active' &&
          (stored.record.staffId !== current.staffId || stored.record.acceptedAt !== current.acceptedAt)) {
        evaluatePrivacy();
        return;
      }
      if (stored.status === 'unavailable') { state = { status: 'unavailable' }; return; }
      forget();
      lastKnownRecord = null;
      fallbackRecord = null;
      state = { status: 'none' };
      queueCart();
    }, Math.max(0, deadline - performance.now()) + 1);
  };
  const reconcile = () => {
    if (privacyStatus() !== 'allowed') return;
    const newCandidate = validId(candidate) && !candidateUsed;
    if (newCandidate) candidateUsed = true;
    const stored = readRecord();
    let record = stored.status === 'active' ? stored.record : null;
    if (fallbackRecord && Date.now() < fallbackRecord.expiresAt &&
        (!record || fallbackRecord.acceptedAt >= record.acceptedAt)) record = fallbackRecord;
    if (stored.status === 'unavailable' && lastKnownRecord && Date.now() < lastKnownRecord.expiresAt &&
        (!record || lastKnownRecord.acceptedAt > record.acceptedAt)) record = lastKnownRecord;
    if (newCandidate) {
      const now = Date.now();
      record = { version: 2, staffId: candidate, acceptedAt: now, expiresAt: now + ttl };
      fallbackRecord = writeRecord(record) ? null : record;
    }
    if (!record && stored.status === 'unavailable') {
      clearTimeout(expiryTimer);
      if (lastKnownRecord && Date.now() >= lastKnownRecord.expiresAt) {
        lastKnownRecord = null;
        state = { status: 'none' };
        queueCart();
      } else state = { status: 'unavailable' };
      return;
    }
    lastKnownRecord = record;
    if (state.status === 'active' && record &&
        (state.staffId !== record.staffId || state.acceptedAt !== record.acceptedAt)) stopCartWork();
    setState(record);
    queueCart();
  };

  async function synchronizeCart(signal) {
    if (signal.aborted || privacyStatus() === 'unknown' || !['active', 'none', 'denied'].includes(state.status)) return false;
    const value = activeId() || '';
    setField(value);
    for (let attempt = 0; attempt < 3; attempt++) {
      if (signal.aborted) return false;
      try {
        const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal });
        if (!response.ok) throw new Error('cart read failed');
        const cart = await response.json();
        if ((cart.attributes?.Stuff || '') === value) return true;
        if (signal.aborted) return false;
        const update = await fetch(`${root}cart/update.js`, {
          method: 'POST', credentials: 'same-origin', signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { Stuff: value } }),
        });
        if (!update.ok) {
          if ([409, 429, 500, 502, 503, 504].includes(update.status)) throw new Error('temporary cart failure');
          return false;
        }
        const saved = await update.json();
        if ((saved.attributes?.Stuff || '') === value) return true;
      } catch { if (signal.aborted) return false; }
      await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
    }
    return false;
  }
  const queueCart = (epoch = cartEpoch) => {
    const signal = cartController.signal;
    cartWork = cartWork.catch(() => false).then(() => epoch === cartEpoch ? synchronizeCart(signal) : false);
    return cartWork;
  };

  const evaluatePrivacy = () => {
    const permission = privacyStatus();
    if (permission === 'unknown') return;
    if (permission === 'allowed') { reconcile(); return; }
    clearTimeout(expiryTimer);
    forget();
    lastKnownRecord = null;
    fallbackRecord = null;
    stopCartWork();
    state = { status: 'denied' };
    queueCart();
  };
  const loadPrivacy = () => {
    if (privacyLoading || privacyLoaded) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') return;
    privacyLoading = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        privacyLoading = false;
        if (error || typeof window.Shopify?.customerPrivacy?.marketingAllowed !== 'function') return;
        privacyLoaded = true;
        evaluatePrivacy();
      });
    } catch { privacyLoading = false; }
  };

  document.addEventListener('visitorConsentCollected', evaluatePrivacy);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', loadPrivacy);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) evaluatePrivacy(); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) { candidateUsed = true; evaluatePrivacy(); } });
  window.addEventListener('storage', (event) => { if (event.key === key || event.key === null) evaluatePrivacy(); });
  document.addEventListener('DOMContentLoaded', () => {
    loadPrivacy();
    let attempts = 0;
    const waitForPrivacy = setInterval(() => {
      loadPrivacy();
      if (privacyLoaded || ++attempts >= 20) clearInterval(waitForPrivacy);
    }, 250);
    const cartItems = document.querySelector('cart-items');
    if (cartItems) new MutationObserver(() => {
      if (state.status === 'active') setField(activeId() || '');
      else if (state.status === 'none' || state.status === 'denied') setField('');
    }).observe(cartItems, { childList: true, subtree: true });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => queueCart());
    }
    let checkoutPending = false;
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      const button = event.submitter;
      if (form?.id !== 'cart' || button?.name !== 'checkout' || form.dataset.staffChecked === '1') return;
      event.preventDefault();
      if (checkoutPending) return;
      checkoutPending = true;
      let finished = false;
      let timerId;
      const timer = new Promise((resolve) => { timerId = setTimeout(() => {
        finished = true;
        stopCartWork();
        resolve(false);
      }, 1500); });
      const work = (async () => {
        while (privacyStatus() === 'unknown' && !finished) {
          await new Promise((resolve) => setTimeout(resolve, 20));
        }
        if (finished) return false;
        const epoch = cartEpoch;
        evaluatePrivacy();
        return epoch === cartEpoch ? queueCart() : cartWork;
      })();
      try {
        await Promise.race([work, timer]);
        finished = true;
        clearTimeout(timerId);
        form.dataset.staffChecked = '1';
        form.requestSubmit(button);
      } finally {
        finished = true;
        clearTimeout(timerId);
        delete form.dataset.staffChecked;
        checkoutPending = false;
      }
    }, true);
  });
})();
